// src/hooks/useAppShell.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export function useAppShell() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    // =========================================================
    // 1. ESTADOS DE INTERFAZ (UI)
    // =========================================================
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openNotas, setOpenNotas] = useState(false);

    // =========================================================
    // 2. ESTADOS DEL ESCUDO DE FICHAJE LEGAL Y SEGURIDAD
    // =========================================================
    const [isFichajeActivo, setIsFichajeActivo] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [motivoModal, setMotivoModal] = useState('manual'); // 'manual' | 'inactividad'

    const userRole = user?.role || user?.rol || 'medico';
    // FIX RBAC: 'admin' incluido en la visión administrativa
    const isAdmin = ['supermedico', 'recepcion', 'admin'].includes(userRole);

    // =========================================================
    // 3. CONTROL DE FLUJO: CIERRE DE SESIÓN SEGURO
    // =========================================================
    const comprobarEstadoYMostrarModal = useCallback((motivo = 'manual') => {
        setIsMenuOpen(false);
        setMobileOpen(false);

        // Si el reloj está corriendo, levantamos el escudo modal. Si no, cerramos sesión directo.
        if (isFichajeActivo) {
            setMotivoModal(motivo);
            setShowLogoutModal(true);
        } else {
            logout();
        }
    }, [isFichajeActivo, logout]);

    // =========================================================
    // 4. MOTOR DE AUDITORÍA: VIGILANTE DE INACTIVIDAD (30 MIN)
    // =========================================================
    useEffect(() => {
        let inactividadTimeout;
        const resetInactividad = () => {
            clearTimeout(inactividadTimeout);
            inactividadTimeout = setTimeout(() => {
                comprobarEstadoYMostrarModal('inactividad');
            }, 30 * 60 * 1000);
        };

        // Escuchamos la interacción física del médico con el equipo
        window.addEventListener('mousemove', resetInactividad);
        window.addEventListener('keydown', resetInactividad);
        window.addEventListener('click', resetInactividad);
        resetInactividad();

        return () => {
            window.removeEventListener('mousemove', resetInactividad);
            window.removeEventListener('keydown', resetInactividad);
            window.removeEventListener('click', resetInactividad);
            clearTimeout(inactividadTimeout);
        };
    }, [comprobarEstadoYMostrarModal]);

    // =========================================================
    // 5. ACCIONES DEFINITIVAS DE CIERRE
    // =========================================================
    const handleLogoutConCierre = async () => {
        setIsLoggingOut(true);
        try {
            await api.post('/fichajes/salida');
        } catch (e) {
            console.error('Error cerrando el turno de trabajo:', e);
        } finally {
            logout();
        }
    };

    const handleLogoutSinCierre = () => {
        logout();
    };

    return {
        user, userRole, isAdmin,
        location, navigate,
        mobileOpen, setMobileOpen,
        isMenuOpen, setIsMenuOpen,
        openNotas, setOpenNotas,
        setIsFichajeActivo,
        showLogoutModal, setShowLogoutModal,
        isLoggingOut, motivoModal,
        comprobarEstadoYMostrarModal,
        handleLogoutConCierre, handleLogoutSinCierre
    };
}